import { Network } from '@wren/shared';
import { CartWithItems, SerializedUser } from '../types';
import { isCustomDurationCartItem, isSubscriptionCartItem } from './cart';

// order matters here!
const CHECKOUT_STEPS = [
  '/checkout/create-account' as const,
  '/checkout/create-team' as const,
  '/checkout/add-ons' as const,
  '/checkout/source-survey' as const,
  '/checkout/payment' as const,
];

interface Options {
  skipSourceSurvey?: boolean;
  skipAddOns?: boolean;
  isAddOnDetailPage?: boolean;
}

export async function validateCartForPayment() {
  const [response, responseBody] = await Network.get<{
    message?: string;
    code?: string;
  }>('stripe/validate-cart-for-payment');

  if (response.ok) return;

  if (responseBody?.code === 'team_selection_required') {
    window.location.assign('/checkout/create-team');
  }

  throw new Error(
    responseBody?.message ??
      'We could not validate this cart. Please refresh and try again.'
  );
}

export function isBusinessCheckoutItem(item: CartWithItems['items'][number]) {
  const details = item.detailsJson as {
    forTeam?: boolean;
    requiresTeam?: boolean;
  } | null;
  return details?.requiresTeam === true && details.forTeam !== false;
}

export function getCheckoutSteps(
  cart: CartWithItems | null,
  options?: Options
): typeof CHECKOUT_STEPS {
  if (!cart) return [];

  let steps = [...CHECKOUT_STEPS];

  if (!cart.items.some(isBusinessCheckoutItem)) {
    steps = steps.filter((step) => step !== '/checkout/create-team');
  }

  if (!cart.items.some(isSubscriptionCartItem) || options?.skipAddOns) {
    steps = steps.filter((step) => step !== '/checkout/add-ons');
  }
  if (options?.skipSourceSurvey) {
    steps = steps.filter((step) => step !== '/checkout/source-survey');
  }

  return steps;
}

export function isPathInCheckoutSteps(
  pathname: string,
  checkoutSteps: typeof CHECKOUT_STEPS
): pathname is (typeof CHECKOUT_STEPS)[number] {
  return checkoutSteps.includes(pathname as (typeof CHECKOUT_STEPS)[number]);
}

export function getNextStep(
  pathname: string,
  cart: CartWithItems | null,
  options?: Options
) {
  const checkoutSteps = getCheckoutSteps(cart, options);

  if (!isPathInCheckoutSteps(pathname, checkoutSteps)) {
    // Not sure what to do here.
    return pathname;
  }

  const currentStepIndex = checkoutSteps.indexOf(pathname);
  const nextStep = checkoutSteps[currentStepIndex + 1];

  if (!nextStep) {
    // Not sure what to do here.
    return pathname;
  }

  return nextStep;
}

export function getPreviousStep(
  pathname: string,
  cart: CartWithItems | null,
  options?: Options
) {
  const checkoutSteps = getCheckoutSteps(cart, options);

  if (!isPathInCheckoutSteps(pathname, checkoutSteps)) {
    // Not sure what to do here.
    return pathname;
  }
  // if we're on the add-ons detail page, the "previous step" is the same URL
  if (options?.isAddOnDetailPage) {
    return '/checkout/add-ons';
  }

  const currentStepIndex = checkoutSteps.indexOf(pathname);
  const previousStep = checkoutSteps[currentStepIndex - 1];

  // You can only create an account once.
  if (!previousStep || previousStep === '/checkout/create-account') {
    return null;
  }

  return previousStep;
}

export function getMostAppropriateCheckoutStep(
  user: SerializedUser,
  cart: CartWithItems,
  options?: Options
) {
  const isIncomplete: { [path in (typeof CHECKOUT_STEPS)[number]]: boolean } = {
    '/checkout/create-account': !user.hasPassword,
    '/checkout/create-team':
      !cart.teamId && cart.items.some(isBusinessCheckoutItem),
    '/checkout/add-ons':
      !cart.items.some(isCustomDurationCartItem) && !user.importedFromKlima,
    '/checkout/source-survey': !user.source,
    '/checkout/payment': true,
  };

  const checkoutSteps = getCheckoutSteps(cart, options);

  return checkoutSteps.find((step) => isIncomplete[step]) || checkoutSteps[0];
}
